import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { electronApis } from '@/features/electron-api';
import {
  Callout,
  FullDiskAccessHint,
  isFullDiskAccessSetupFailure,
  useRerunSetupWhenFullDiskAccessGranted,
} from '@/features/local-mcp';
import {
  useSetupEnding,
  useSetupProgress,
  type SetupEnding,
} from '@/features/local-mcp/use-setup-progress';
import { fullDiskAccessPermissionQueryOptions } from '@/query/permissions';

import type { ServerEntry } from '@littlebird/axon-mcp';

export const JUST_FINISHED_MS = 4000;

// "8,400/20,000 indexed" while running, "Indexed 20,000 of 20,000 emails" as the last line
const PROGRESS_COUNT = /([\d,]+)\s*(?:\/|\bof\b)\s*([\d,]+)/;

function parsePercent(line: string | undefined): number | undefined {
  const match = line ? PROGRESS_COUNT.exec(line) : null;
  if (!match) return undefined;
  const done = Number(match[1].replaceAll(',', ''));
  const total = Number(match[2].replaceAll(',', ''));
  if (!(total > 0)) return undefined;
  return Math.min(100, Math.round((done / total) * 100));
}

interface RunOutcome {
  justFinished: boolean;
  /** The run ended but the refetched `lastSetup` has not arrived yet. */
  awaitingStamp: boolean;
}

/**
 * `useSetupProgress` drops the slug and invalidates the servers query in the
 * same event, so right after a run `lastSetup` is still the previous run's
 * stamp. The stamp seen when the run started is latched; the outcome counts
 * only once `finishedAt` moves past it. A cancelled run writes no stamp, so
 * its end releases the latch at once.
 */
function useRunOutcome(
  running: boolean,
  lastSetup: ServerEntry['lastSetup'],
  ending: SetupEnding | undefined
): RunOutcome {
  const [latched, setLatched] = useState<{ finishedAt?: string } | null>(
    running ? { finishedAt: lastSetup?.finishedAt } : null
  );
  const [justFinished, setJustFinished] = useState(false);

  if (running && latched === null) {
    setLatched({ finishedAt: lastSetup?.finishedAt });
  } else if (!running && latched !== null && ending === 'cancelled') {
    setLatched(null);
  } else if (
    !running &&
    latched !== null &&
    lastSetup?.finishedAt !== latched.finishedAt
  ) {
    setLatched(null);
    setJustFinished(lastSetup?.ok === true);
  }

  useEffect(() => {
    if (!justFinished) return;
    const timer = setTimeout(() => setJustFinished(false), JUST_FINISHED_MS);
    return () => clearTimeout(timer);
  }, [justFinished]);

  return { justFinished, awaitingStamp: !running && latched !== null };
}

function SetupProgressBar({ line }: { line: string }) {
  const percent = parsePercent(line);
  return (
    <YStack className="gap-1.5">
      <div
        className="h-1 overflow-hidden rounded-full bg-secondary"
        role="progressbar"
        aria-label={percent === undefined ? 'Starting' : 'Progress'}
        {...(percent !== undefined && {
          'aria-valuenow': percent,
          'aria-valuemin': 0,
          'aria-valuemax': 100,
        })}
      >
        <div
          className={
            percent === undefined
              ? 'h-full w-full animate-pulse rounded-full bg-foreground-muted'
              : 'h-full rounded-full bg-foreground-muted transition-[width]'
          }
          style={percent === undefined ? undefined : { width: `${percent}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-foreground-muted">
        {percent === undefined ? 'Starting…' : line}
      </span>
    </YStack>
  );
}

function SetupStatusLine({
  server,
  justFinished,
}: {
  server: ServerEntry;
  justFinished: boolean;
}) {
  const last = server.lastSetup;
  if (!last?.ok) return null;
  if (justFinished) {
    return (
      <XStack className="items-center gap-1.5 text-xs text-success-11">
        <Icons.check className="size-3.5" aria-hidden="true" />
        <span>Up to date{last.detail ? ` · ${last.detail}` : ''}</span>
      </XStack>
    );
  }
  const when = formatDistanceToNow(new Date(last.finishedAt), {
    addSuffix: true,
  });
  return (
    <p className="text-xs text-foreground-muted">
      {last.detail ? `${last.detail} · ${when}` : `Last run ${when}`}
    </p>
  );
}

function SetupFailure({
  server,
  actionLabel,
  progress,
}: {
  server: ServerEntry;
  actionLabel: string;
  progress: Record<string, string>;
}) {
  const fdaQuery = useQuery(fullDiskAccessPermissionQueryOptions());
  useRerunSetupWhenFullDiskAccessGranted(fdaQuery.data, [server], progress);
  const fdaShaped = isFullDiskAccessSetupFailure(server);
  const granted = fdaQuery.data === true;

  let detail: React.ReactNode = null;
  if (fdaShaped && !granted) {
    detail = <FullDiskAccessHint granted={false} />;
  } else if (fdaShaped) {
    detail = (
      <p className="text-xs leading-relaxed text-foreground-muted">
        Full Disk Access is on now. {actionLabel} to finish.
      </p>
    );
  } else if (server.lastSetup?.detail) {
    detail = (
      <p className="text-xs leading-relaxed text-foreground-muted">
        {server.lastSetup.detail}
      </p>
    );
  }

  return (
    <Callout tone="warning" icon={<Icons.warn className="size-4" />}>
      <YStack className="min-w-0 gap-0.5">
        <span className="text-sm font-medium text-foreground">
          {server.setup?.label} failed
        </span>
        {detail}
      </YStack>
    </Callout>
  );
}

export function ServerSetupSection({
  server,
  actionLabel = 'Re-run setup',
  runningLabel = 'Running…',
}: {
  server: ServerEntry;
  actionLabel?: string;
  runningLabel?: string;
}) {
  const progress = useSetupProgress();
  const running = server.slug in progress;
  const { justFinished, awaitingStamp } = useRunOutcome(
    running,
    server.lastSetup,
    useSetupEnding(server.slug)
  );

  if (!server.setup) return null;

  return (
    <YStack className="gap-2">
      {server.lastSetup?.ok === false && !running && !awaitingStamp && (
        <SetupFailure
          server={server}
          actionLabel={actionLabel}
          progress={progress}
        />
      )}
      <YStack className="gap-2.5 rounded-lg border border-border p-3">
        <XStack className="items-center justify-between">
          <span className="text-sm font-medium text-foreground">
            {server.setup.label}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={running}
            onClick={() => void electronApis.axonMcp.runSetup(server.slug)}
          >
            {running ? runningLabel : actionLabel}
          </Button>
        </XStack>
        {running ? (
          <SetupProgressBar line={progress[server.slug]} />
        ) : awaitingStamp ? null : (
          <SetupStatusLine server={server} justFinished={justFinished} />
        )}
      </YStack>
    </YStack>
  );
}
